// frontend/src/pages/student/StudentDashboard.js (نسخه نهایی و کامل)

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axiosInstance from '../../modules/api'; // ✅ فقط از axiosInstance استفاده می‌کنیم
import { 
    FaBook, FaClipboardList, FaCheckCircle, FaHourglassHalf, 
    FaSpinner, FaBookOpen, FaUsers // ✅ FaUsers برای گروه‌ها اضافه شد
} from 'react-icons/fa';
import StudentHeader from '../../components/layout/StudentHeader';
import '../../assets/styles/global.css';

const StudentDashboard = () => {
    const [summaryData, setSummaryData] = useState({});
    const [assignments, setAssignments] = useState([]);
    const [courses, setCourses] = useState([]);
    const [groups, setGroups] = useState([]); // ✅ State جدید برای گروه‌ها
    
    // ✅ Loading به آبجکت تبدیل شد تا هر بخش جداگانه لود شود
    const [loading, setLoading] = useState({ 
        summary: true, 
        assignments: true, 
        courses: true, 
        groups: true // ✅ لودینگ گروه‌ها
    });
    
    // ✅ setError در سطح کامپوننت تعریف شد
    const [error, setError] = useState(null); 

    useEffect(() => {
        // ✅ فقط یک تابع fetchData وجود دارد
        const fetchDashboardData = async () => {
            // ✅ setLoading به درستی در ابتدا تنظیم شد
            setLoading({ summary: true, assignments: true, courses: true, groups: true });
            try {
                // استفاده از Promise.all برای درخواست‌های همزمان
                const [summaryRes, assignmentsRes, coursesRes, groupsRes] = await Promise.all([
                    axiosInstance.get(`/core/student/dashboard-summary/`),
                    axiosInstance.get(`/core/student/assignments/`),
                    axiosInstance.get(`/core/student/courses/`),
                    axiosInstance.get(`/core/student/my-groups/`) // ✅ فراخوانی API جدید گروه‌ها
                ]);

                setSummaryData(summaryRes.data);
                setAssignments(assignmentsRes.data);
                setCourses(coursesRes.data);
                setGroups(groupsRes.data); // ✅ ذخیره گروه‌ها

            } catch (err) {
                console.error("خطا در دریافت اطلاعات داشبورد:", err);
                setError("ارتباط با سرور برقرار نشد. لطفاً اتصال اینترنت خود را بررسی کنید.");
            } finally {
                // ✅ تنظیم همه‌ی لودینگ‌ها به false
                setLoading({ summary: false, assignments: false, courses: false, groups: false });
            }
        };

        fetchDashboardData();
    }, []); // ✅ فقط یکبار در زمان بارگذاری اجرا می‌شود

    const getStatus = (dueDate) => {
        const isPending = new Date(dueDate) > new Date();
        return {
            text: isPending ? "باقی مانده" : "پایان یافته",
            icon: isPending ? <FaHourglassHalf className="status-icon pending" /> : <FaCheckCircle className="status-icon completed" />
        };
    };

    const renderWidgetValue = (value, isLoading) => (isLoading ? <FaSpinner className="spinner" /> : value);
    
    const renderCoursesList = () => {
        if (loading.courses) {
            return <p className="text-center py-4 text-gray-400"><FaSpinner className="spinner" /> در حال بارگذاری دوره‌ها...</p>;
        }
        if (courses.length === 0) {
            return <p className="text-center py-4 text-gray-500">در هیچ دوره‌ای ثبت‌نام نکرده‌اید.</p>;
        }

        return (
            <div className="courses-grid"> 
                {courses.map(course => ( // ✅ متغیر 'course' است
                    <Link 
                        key={course.id} // ✅✅✅ اصلاح شد: از course.id استفاده می‌کند
                        to={`/student/courses/${course.id}/lessons/`} 
                        className="course-card"
                    >
                        <FaBookOpen className="text-4xl text-blue-500 mb-2" />
                        <h3 className="text-lg font-semibold">{course.title}</h3>
                        <p className="text-sm text-gray-400">
                            پایه: {course.grade ? course.grade.name : 'نامشخص'}
                        </p>
                        <span className="text-blue-500 font-medium mt-auto">ورود به درس‌ها &larr;</span>
                    </Link>
                ))}
            </div>
        );
    };

    const renderAssignmentsTable = () => {
        if (loading.assignments) {
            return <tr><td colSpan="4" className="text-center"><FaSpinner className="spinner" /> در حال بارگذاری تکالیف...</td></tr>;
        }
        if (assignments.length === 0) {
            return <tr><td colSpan="4" className="text-center">در حال حاضر هیچ تکلیفی برای شما وجود ندارد.</td></tr>;
        }
        return assignments.map(assignment => {
            const status = getStatus(assignment.due_date);
            return (
                <tr key={assignment.id} className="clickable-row">
                    <td><Link to={`/student/assignment/${assignment.id}`}>{assignment.title}</Link></td>
                    <td>{assignment.lesson_title || 'درس نامشخص'}</td>
                    <td>{new Date(assignment.due_date).toLocaleString('fa-IR', { day: '2-digit', month: 'long', hour: '2-digit', minute: '2-digit' })}</td>
                    <td><div className="status-cell">{status.icon}<span>{status.text}</span></div></td>
                </tr>
            );
        });
    };

    const renderGroupsList = () => {
        if (loading.groups) {
            return <p className="text-center py-4 text-gray-400"><FaSpinner className="spinner" /> در حال بارگذاری گروه‌ها...</p>;
        }
        if (groups.length === 0) {
            return <p className="text-center py-4 text-gray-500">شما در هیچ گروهی عضو نیستید.</p>;
        }
        return (
            <div className="courses-grid">
                {groups.map(group => ( // ✅ متغیر 'group' است
                    <Link 
                        key={group.id} // ✅ اینجا group.id صحیح است
                        to={`/chat/group/${group.id}`} 
                        className="course-card" 
                        style={{borderRightColor: 'var(--accent-green)'}} 
                    >
                        <FaUsers className="text-4xl text-green-500 mb-2" />
                        <h3 className="text-lg font-semibold">{group.name}</h3>
                        <span className="text-green-500 font-medium mt-auto">ورود به اتاق گفتگو &larr;</span>
                    </Link>
                ))}
            </div>
        );
    };

    return (
        <div className="app-layout-student">
            <StudentHeader />
            <div className="page-content">
                {error && <div className="alert alert-danger">{error}</div>}
                
                <div className="summary-widgets-grid student-widgets">
                    <div className="summary-widget color-orange">
                        <div className="widget-icon-container"><FaBook /></div>
                        <div className="widget-info">
                            <p className="widget-value">{renderWidgetValue(summaryData.enrolled_courses_count, loading.summary)}</p>
                            <p className="widget-label">دوره‌های من</p>
                        </div>
                    </div>
                    <div className="summary-widget color-green">
                        <div className="widget-icon-container"><FaCheckCircle /></div>
                        <div className="widget-info">
                            <p className="widget-value">{renderWidgetValue(summaryData.submitted_assignments_count, loading.summary)}</p>
                            <p className="widget-label">تکالیف ارسال شده</p>
                        </div>
                    </div>
                    <div className="summary-widget color-blue">
                        <div className="widget-icon-container"><FaClipboardList /></div>
                        <div className="widget-info">
                            <p className="widget-value">{renderWidgetValue(summaryData.active_assignments_count, loading.summary)}</p>
                            <p className="widget-label">تکلیف فعال</p>
                        </div>
                    </div>
                </div>

                <h3 className="table-title mt-8 mb-4 border-b pb-2">مطالعه دروس آنلاین</h3>
                {renderCoursesList()}
                
                <h3 className="table-title mt-8 mb-4 border-b pb-2">گروه‌های درسی من</h3>
                {renderGroupsList()}
                
                <div className="data-table-container student-table mt-8">
                    <h3 className="table-title">آخرین تکالیف</h3>
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>عنوان تکلیف</th>
                                <th>درس مرتبط</th>
                                <th>مهلت ارسال</th>
                                <th>وضعیت</th>
                            </tr>
                        </thead>
                        <tbody>
                            {renderAssignmentsTable()}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default StudentDashboard;